import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'qrcode';

import { supabase } from '../lib/supabase';
import { useMyRestaurants } from '../hooks/useMyRestaurants';
import { formatMoney } from '../lib/format';
import { SITE_URL } from '../lib/seo';
import { buildSealSvg, downloadSealPng } from '../lib/seal';

interface Ranking {
  score: number;
  rank: number;
  bucket_size: number;
  revenue_cents: number;
  avg_satisfaction: number;
  turns: number;
  cuisine: string | null;
  region: string | null;
}

export function AwardPage() {
  const { t } = useTranslation();
  const { restaurants, loading } = useMyRestaurants();
  const restaurant = restaurants[0];
  const [r, setR] = useState<Ranking | null>(null);
  const [qr, setQr] = useState<string>('');

  useEffect(() => {
    if (!restaurant) return;
    void supabase.rpc('award_ranking', { p_restaurant_id: restaurant.id }).then(({ data }) => {
      if (data) setR(data as Ranking);
    });
    void QRCode.toDataURL(SITE_URL, { width: 280, margin: 1, color: { dark: '#2b2723', light: '#ffffff' } }).then(setQr);
  }, [restaurant]);

  const year = new Date().getFullYear();
  const subtitle = [r?.cuisine, r?.region, String(year)].filter(Boolean).join(' · ');
  const sealSvg = useMemo(() => {
    if (!restaurant || !qr) return '';
    return buildSealSvg({ name: restaurant.name, subtitle, qrDataUrl: qr });
  }, [restaurant, qr, subtitle]);

  if (loading || !restaurant) return <div className="center-note">{t('common.loading')}</div>;

  return (
    <div className="page">
      <h1 className="page__title">🏆 {t('award.title')}</h1>

      {!r || r.turns === 0 ? (
        <p className="muted-note">{t('award.none')}</p>
      ) : (
        <div className="stat-grid">
          <div className="stat">
            <span className="stat__label">{t('award.your_score')}</span>
            <span className="stat__value">{r.score}</span>
          </div>
          <div className="stat">
            <span className="stat__label">{t('award.rank')}</span>
            <span className="stat__value">{r.rank} {t('award.of')} {r.bucket_size}</span>
          </div>
          <div className="stat">
            <span className="stat__label">{t('award.satisfaction')}</span>
            <span className="stat__value">{r.avg_satisfaction}</span>
          </div>
          <div className="stat">
            <span className="stat__label">{t('award.revenue')}</span>
            <span className="stat__value">{formatMoney(r.revenue_cents, restaurant.currency, restaurant.locale)}</span>
          </div>
          <div className="stat">
            <span className="stat__label">{t('award.turns')}</span>
            <span className="stat__value">{r.turns}</span>
          </div>
        </div>
      )}

      <h2 className="section-title">{t('award.seal_title')}</h2>
      <p className="muted-note">{t('award.seal_desc')}</p>
      {sealSvg && (
        <div className="seal-block">
          <img
            className="seal-preview"
            alt="seal"
            src={`data:image/svg+xml;utf8,${encodeURIComponent(sealSvg)}`}
          />
          <button type="button" className="btn btn--primary" onClick={() => downloadSealPng(sealSvg, `selo-${restaurant.slug}.png`)}>
            {t('award.download_seal')}
          </button>
        </div>
      )}
    </div>
  );
}
